import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { CalendarToday, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  formatLocalDateKey,
  getMonthCalendarDays,
  startOfLocalToday
} from '../utils/calendarDateKeys';

const PriceManagement = ({ yacht }) => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDateKeys, setSelectedDateKeys] = useState([]);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceValue, setPriceValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const yachtDoc = await getDoc(doc(db, 'yachts', yacht.id));
        if (yachtDoc.exists()) {
          const yachtData = yachtDoc.data();
          setAvailability(yachtData.availability || {});
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        setError('Błąd podczas pobierania danych');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [yacht.id]);

  const getDateKey = (date) => formatLocalDateKey(date);

  const getAvailabilityStatus = (date) => {
    const dateKey = getDateKey(date);
    return availability[dateKey]?.status || 'available';
  };

  const getDatePrice = (date) => {
    const dateKey = getDateKey(date);
    return availability[dateKey]?.price || null;
  };

  const isOffSeasonDate = (date) => {
    const month = date.getMonth() + 1; // 1-12
    const day = date.getDate();
    if (month === 10 && day >= 12) return true; // od 12 października
    if (month >= 11 || month <= 3) return true; // listopad-marzec
    return false;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateClick = (date) => {
    const dateKey = getDateKey(date);
    setSelectedDateKeys((prev) =>
      prev.includes(dateKey)
        ? prev.filter((d) => d !== dateKey)
        : [...prev, dateKey]
    );
  };

  const handleOpenBulkPriceDialog = () => {
    if (selectedDateKeys.length === 0) return;
    if (selectedDateKeys.length === 1) {
      const oneDate = new Date(`${selectedDateKeys[0]}T00:00:00`);
      setPriceValue(getDatePrice(oneDate) || '');
    } else {
      setPriceValue('');
    }
    setPriceDialogOpen(true);
  };

  const handleSavePrice = async () => {
    if (selectedDateKeys.length === 0) return;
    
    try {
      const newPrice = priceValue ? Number(priceValue) : null;

      const newAvailability = { ...availability };
      selectedDateKeys.forEach((dateKey) => {
        const currentData = newAvailability[dateKey] || {
          status: 'available',
          price: null
        };
        newAvailability[dateKey] = {
          ...currentData,
          price: newPrice
        };
      });
      
      await updateDoc(doc(db, 'yachts', yacht.id), {
        availability: newAvailability
      });
      
      setAvailability(newAvailability);
      setPriceDialogOpen(false);
      setSelectedDateKeys([]);
      setPriceValue('');
    } catch (error) {
      console.error('Error updating price:', error);
      setError('Błąd podczas zapisywania ceny');
    }
  };

  const monthDays = getMonthCalendarDays(currentMonth);
  const todayStart = startOfLocalToday();
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography>Ładowanie...</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarToday sx={{ mr: 1 }} />
          <Typography variant="h6">
            Zarządzanie Cenami - {yacht.name}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => navigateMonth(-1)}
            sx={{ bgcolor: 'grey.100' }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'center' }}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => navigateMonth(1)}
            sx={{ bgcolor: 'grey.100' }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Klikaj daty, aby je zaznaczyć; potem ustaw cenę hurtowo jednym zapisem.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Poza sezonem (12 października - 31 marca) terminy są niedostępne do edycji.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleOpenBulkPriceDialog}
          disabled={selectedDateKeys.length === 0}
        >
          Ustaw cenę dla zaznaczonych ({selectedDateKeys.length})
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSelectedDateKeys([])}
          disabled={selectedDateKeys.length === 0}
        >
          Wyczyść zaznaczenie
        </Button>
      </Box>

      {/* Day headers */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        mb: 1 
      }}>
        {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Niedz'].map((day) => (
          <Box key={day} sx={{
            p: 2,
            textAlign: 'center',
            borderBottom: '2px solid',
            borderColor: 'grey.200'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        {monthDays.map((date, index) => {
          if (!date) {
            return <Box key={`empty-${index}`} sx={{ height: 80, borderRight: '1px solid', borderColor: 'grey.200' }} />;
          }
          
          const status = getAvailabilityStatus(date);
          const price = getDatePrice(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const isPast = date < todayStart;
          const isOffSeason = isOffSeasonDate(date);
          const isDisabled = isPast || isOffSeason;
          const dateKey = getDateKey(date);
          const isSelected = selectedDateKeys.includes(dateKey);

          return (
            <Box
              key={index}
              sx={{
                height: 80,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                cursor: !isDisabled ? 'pointer' : 'default',
                position: 'relative',
                borderRight: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                bgcolor: isSelected ? 'primary.50' : 'white',
                transition: 'all 0.15s ease',
                ...(isToday && {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .date-number': {
                    color: 'white',
                    fontWeight: 600
                  }
                }),
                ...(isDisabled && {
                  bgcolor: 'grey.50',
                  '& .date-number': {
                    color: 'text.disabled'
                  }
                }),
                ...(!isDisabled && !isToday && {
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.100' : 'grey.50'
                  }
                })
              }}
              onClick={() => !isDisabled && handleDateClick(date)}
            >
              {/* Date number */}
              <Typography 
                variant="body2" 
                className="date-number"
                sx={{ 
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: price ? 0.5 : 0
                }}
              >
                {date.getDate()}
              </Typography>
              
              {/* Price display */}
              {price && !isDisabled && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 500,
                    color: isToday ? 'white' : 'primary.main',
                    textAlign: 'center',
                    lineHeight: 1
                  }}
                >
                  {price} zł
                </Typography>
              )}
              
              {/* Subtle status indicator */}
              {!isDisabled && !isToday && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: isSelected ? 12 : 4,
                    right: 4,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor:
                      (status === 'changeover' ? 'changeover_start' : status) === 'available'
                        ? 'success.main'
                        : (status === 'changeover' ? 'changeover_start' : status) === 'changeover_start'
                          ? 'info.main'
                          : (status === 'changeover' ? 'changeover_start' : status) === 'changeover_end'
                            ? 'secondary.main'
                          : 'warning.main'
                  }}
                />
              )}
              {isSelected && !isToday && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main'
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 4, 
        mt: 3,
        p: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main'
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Dostępny
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'warning.main'
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Zajęty
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main'
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Dzisiaj
          </Typography>
        </Box>
      </Box>

      {/* Price Dialog */}
      <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDateKeys.length === 1
            ? `Ustaw cenę dla ${new Date(`${selectedDateKeys[0]}T00:00:00`).toLocaleDateString('pl-PL')}`
            : `Ustaw cenę dla ${selectedDateKeys.length} zaznaczonych dat`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Cena (zł)"
              type="number"
              fullWidth
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              placeholder="Wprowadź cenę lub zostaw puste"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Zostaw puste, aby usunąć cenę dla tej daty
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleSavePrice} variant="contained" color="primary">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PriceManagement;
